import { useEffect, useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import { Upload, UserPlus, CheckCircle2, AlertTriangle, Send } from 'lucide-react'
import { supabase, isDemoMode } from '../../lib/supabase'
import { DemoNotice } from '../../lib/demo'

// Try to match a user's "Location" string to one of the existing
// companies. We do case-insensitive substring matching both ways:
// "Bude Holiday Resort" appears in "Bude Holiday Resort, Maer Lane, ...".
function matchCompany(location, companies) {
  if (!location) return null
  const loc = location.toLowerCase()
  // Prefer the longest company name that appears in the location string.
  return companies
    .map(c => ({ c, n: c.name.toLowerCase() }))
    .filter(({ n }) => loc.includes(n) || n.includes(loc))
    .sort((a, b) => b.n.length - a.n.length)
    [0]?.c || null
}

function isCentralTeam(location) {
  return (location || '').trim().toLowerCase().startsWith('central team')
}

export default function ImportClients() {
  const [companies, setCompanies] = useState([])
  const [rows, setRows] = useState([])     // parsed XLSX rows
  const [centralCompanyIds, setCentralCompanyIds] = useState([])
  const [sending, setSending] = useState(false)
  const [results, setResults] = useState(null)

  useEffect(() => {
    if (isDemoMode) return
    supabase.from('companies').select('id, name, brand_settings_id').is('archived_at', null).order('name')
      .then(({ data }) => {
        const list = data || []
        setCompanies(list)
        // Default: Central Team gets linked to every company that shares
        // a brand profile (i.e. every site belonging to the same client).
        // For Away Resorts that's all 15 sites.
        const brandedIds = list.filter(c => c.brand_settings_id).map(c => c.id)
        setCentralCompanyIds(brandedIds.length > 0 ? brandedIds : list.map(c => c.id))
      })
  }, [])

  const onFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const buf = await file.arrayBuffer()
    const wb = XLSX.read(buf)
    const sheet = wb.Sheets[wb.SheetNames[0]]
    const raw = XLSX.utils.sheet_to_json(sheet, { defval: '' })
    const norm = raw.map(r => {
      // Tolerate column-name variants.
      const name     = (r.Name || r.name || r.FullName || r['Full Name'] || '').toString().trim()
      const email    = (r.Email || r.email || '').toString().trim().toLowerCase()
      const location = (r.Location || r.location || r.Site || r.site || '').toString().trim()
      const matched  = isCentralTeam(location) ? null : matchCompany(location, companies)
      return {
        name, email, location,
        matched_company_id: matched?.id || '',
        central: isCentralTeam(location),
        include: !!email,
      }
    })
    setRows(norm)
    setResults(null)
  }

  const stats = useMemo(() => {
    const included = rows.filter(r => r.include)
    return {
      total: rows.length,
      included: included.length,
      unmatched: included.filter(r => !r.central && !r.matched_company_id).length,
      central: included.filter(r => r.central).length,
    }
  }, [rows])

  const send = async () => {
    if (isDemoMode) return
    if (stats.unmatched > 0) {
      if (!confirm(`${stats.unmatched} row(s) have no matched company — they'll be invited but linked to nothing. Continue?`)) return
    }
    setSending(true)
    setResults(null)
    try {
      const payload = {
        users: rows.filter(r => r.include).map(r => ({
          email: r.email,
          full_name: r.name,
          company_ids: r.central ? centralCompanyIds : (r.matched_company_id ? [r.matched_company_id] : []),
        })),
      }
      const { data, error } = await supabase.functions.invoke('invite-users', { body: payload })
      if (error) throw error
      setResults(data)
    } catch (err) {
      alert(err.message)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <DemoNotice />

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1">Import Clients</h3>
        <p className="text-sm text-gray-500 mb-4">
          Upload an XLSX with <code>Name</code>, <code>Email</code>, <code>Location</code> columns.
          Each user gets a magic-link invite email (Supabase's templated message — customise it in
          the Supabase dashboard). Users with "Central Team" in the Location column will be linked
          to <em>all</em> sites belonging to that client.
        </p>

        <label className="btn-primary cursor-pointer inline-flex">
          <Upload className="w-4 h-4" /> Upload XLSX
          <input type="file" accept=".xlsx,.xls" onChange={onFile} className="hidden" />
        </label>

        {rows.length > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 uppercase">Rows</div>
              <div className="font-semibold text-gray-800">{stats.total}</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 uppercase">To invite</div>
              <div className="font-semibold text-gray-800">{stats.included}</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 uppercase">Central Team</div>
              <div className="font-semibold text-gray-800">{stats.central}</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 uppercase">Unmatched</div>
              <div className={`font-semibold ${stats.unmatched ? 'text-amber-700' : 'text-gray-800'}`}>{stats.unmatched}</div>
            </div>
          </div>
        )}
      </div>

      {rows.length > 0 && stats.central > 0 && (
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-1">Central Team → which sites?</h3>
          <p className="text-sm text-gray-500 mb-3">
            Anyone whose Location says "Central Team" will be linked to every company you tick below.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-auto p-2 bg-gray-50 rounded-lg">
            {companies.map(c => (
              <label key={c.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={centralCompanyIds.includes(c.id)}
                  onChange={e => {
                    if (e.target.checked) setCentralCompanyIds(ids => [...ids, c.id])
                    else setCentralCompanyIds(ids => ids.filter(x => x !== c.id))
                  }}
                />
                <span className="truncate">{c.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Preview</h3>
            <button onClick={send} disabled={sending || stats.included === 0} className="btn-primary">
              <Send className="w-4 h-4" /> {sending ? 'Sending invites…' : `Send ${stats.included} invite${stats.included === 1 ? '' : 's'}`}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                  <th className="text-left py-2 w-10"></th>
                  <th className="text-left py-2">Name</th>
                  <th className="text-left py-2">Email</th>
                  <th className="text-left py-2">Location</th>
                  <th className="text-left py-2">Linked to</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2 pr-2">
                      <input
                        type="checkbox" checked={r.include}
                        onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, include: e.target.checked } : x))}
                      />
                    </td>
                    <td className="py-2 pr-2 truncate max-w-[200px]">{r.name}</td>
                    <td className="py-2 pr-2 truncate max-w-[240px] text-gray-700">{r.email}</td>
                    <td className="py-2 pr-2 truncate max-w-[260px] text-gray-500">{r.location}</td>
                    <td className="py-2 pr-2">
                      {r.central
                        ? <span className="inline-flex items-center gap-1 text-xs text-brand-dark bg-brand-50 px-2 py-0.5 rounded">All sites ({centralCompanyIds.length})</span>
                        : (
                          <select
                            value={r.matched_company_id}
                            onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, matched_company_id: e.target.value } : x))}
                            className="input-field text-sm py-1.5"
                          >
                            <option value="">— unmatched —</option>
                            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        )
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {results && (
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-3">Results</h3>
          <ul className="divide-y divide-gray-100">
            {(results.results || []).map((r, i) => (
              <li key={i} className="py-2 flex items-center gap-2 text-sm">
                {r.ok
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  : <AlertTriangle className="w-4 h-4 text-red-500" />
                }
                <span className="font-mono text-xs text-gray-700">{r.email}</span>
                <span className={r.ok ? 'text-gray-500' : 'text-red-600'}>
                  {r.ok
                    ? (r.invited ? `Invite sent · linked to ${r.companies} site(s)` : `Already existed · linked to ${r.companies} site(s)`)
                    : r.error}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
